import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, ShieldCheck, Zap, Eye, PenLine } from "lucide-react";
import { IMAGES } from "@/lib/site";
import { ReviewForm } from "@/components/site/ReviewForm";

const featured = [
  { name: "Marie Chen", role: "Fashion Retail Co.", rating: 5, text: "King of Sourcing a transformé notre chaîne d'approvisionnement. Qualité impeccable, délais respectés, et un service client extraordinaire." },
  { name: "Jean Dubois", role: "Tech Imports Ltd.", rating: 3, text: "Nous avons réduit nos coûts de 30% tout en améliorant la qualité. L'équipe est vraiment professionnelle et fiable." },
  { name: "Lisa Park", role: "Wholesale Distributor", rating: 4, text: "Après 3 ans de partenariat, je recommande King of Sourcing sans hésitation. C'est notre meilleur fournisseur." },
];

const badges = [
  { icon: ShieldCheck, t: "Sécurité", d: "Achats vérifiés" },
  { icon: Zap, t: "Rapidité", d: "Délais optimisés" },
  { icon: Eye, t: "Transparence", d: "Prix clairs" },
];

const Stars = ({ rating, testid }) => (
  <div className="flex items-center gap-1 mb-4 text-brand-goldbright" data-testid={testid}>
    {[...Array(5)].map((_, k) => (
      <Star key={k} size={16} fill={k < rating ? "currentColor" : "none"} className={k < rating ? "" : "text-white/25"} />
    ))}
    <span className="ml-1.5 text-xs font-medium text-white/50">{rating}.0</span>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MediaGrid = ({ media, testid }) => {
  if (!media || media.length === 0) return null;
  return (
    <div className="mt-4 grid grid-cols-3 gap-2" data-testid={testid}>
      {media.map((m, i) => (
        m.kind === "video" ? (
          <video
            key={i}
            src={`${BACKEND_URL}${m.url}`}
            controls
            playsInline
            className="h-20 w-full rounded-md object-cover bg-black/40 border border-white/10"
            data-testid={`${testid}-video-${i}`}
          />
        ) : (
          <a key={i} href={`${BACKEND_URL}${m.url}`} target="_blank" rel="noopener noreferrer" className="block" data-testid={`${testid}-image-${i}`}>
            <img
              src={`${BACKEND_URL}${m.url}`}
              alt="Colis reçu"
              loading="lazy"
              className="h-20 w-full rounded-md object-cover border border-white/10 hover:opacity-90 transition-opacity"
            />
          </a>
        )
      ))}
    </div>
  );
};

const ReviewCard = ({ r, i, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
    data-testid={testid}
    className="rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 p-7"
  >
    <Stars rating={r.rating} testid={`${testid}-rating`} />
    <p className="text-white/80 text-sm leading-relaxed">"{r.text}"</p>
    <MediaGrid media={r.media} testid={`${testid}-media`} />
    <div className="mt-6">
      <p className="font-display font-semibold text-white">{r.name}</p>
      <p className="text-xs text-white/50">{r.role || r.company || "Client vérifié"}</p>
    </div>
  </motion.div>
);

export const Testimonials = () => {
  const [community, setCommunity] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews`);
      setCommunity(data || []);
    } catch (e) {
      setCommunity([]);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <section id="avis" className="relative py-24 lg:py-32 overflow-hidden">
      <img src={IMAGES.port} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-brand-navy/90" />
      <div className="grain-overlay" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-brand-goldbright mb-4">Ils nous font confiance</p>
            <h2 className="font-display font-bold uppercase tracking-tight leading-none text-white text-4xl sm:text-5xl">
              Avis de nos clients
            </h2>
          </div>
          <button
            onClick={() => setOpen(true)}
            data-testid="open-review-form-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-goldbright px-6 py-3 text-sm font-bold uppercase tracking-wide text-brand-navy hover:-translate-y-1 transition-transform duration-300 shrink-0"
          >
            <PenLine size={16} /> Laisser un avis
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          {featured.map((r, i) => (
            <ReviewCard key={r.name} r={r} i={i} testid={`review-card-${i}`} />
          ))}
        </div>

        {community.length > 0 && (
          <>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-white/40 mb-5" data-testid="community-reviews-heading">
              Derniers avis clients
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-14" data-testid="community-reviews">
              {community.map((r, i) => (
                <ReviewCard key={r.id} r={r} i={i} testid={`community-review-${i}`} />
              ))}
            </div>
          </>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
          {badges.map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.t} className="flex items-center gap-4 rounded-lg border border-white/10 p-5" data-testid={`trust-${b.t}`}>
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-brand-goldbright text-brand-navy">
                  <Icon size={20} />
                </div>
                <div>
                  <p className="font-display font-semibold text-white">{b.t}</p>
                  <p className="text-xs text-white/50">{b.d}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <ReviewForm open={open} onOpenChange={setOpen} />
    </section>
  );
};
